/**
 * Sidebar — Ultra-modern dark sidebar with context selector
 * Design: Dark glass sidebar, gradient accents, smooth transitions
 */

import { NavLink, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft, ChevronRight, ChevronDown,
  Settings, Users, Lock, CreditCard, Crown, BrainCircuit, Gauge,
  Building2, Plug, ShieldCheck, Gavel, Eye, FileCheck, AlertTriangle,
  Globe, HelpCircle, KeyRound, Network, UserCog,
  LayoutDashboard, LayoutGrid, Briefcase, Wrench,
  Calculator, Receipt, ClipboardCheck, Banknote, Sparkles, MessageSquare, Palette,
  MessagesSquare, SlidersHorizontal,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
// ClientSelector removed from sidebar — context dropdown lives in TopBar only.
import { useContextStore } from '@/stores/context-store'
import {
  getNavSectionsForContext,
  type NavSectionId,
} from '@/config/contexts'
import { tn } from '@/i18n/navLabels'

// ─── Nav Item Interface ────────────────────────────────
interface NavItem {
  title: string
  icon: React.ElementType
  href?: string
  children?: NavItem[]
}

// ─── Bottom nav (always visible) ───────────────────────
const bottomNavItems: NavItem[] = [
  // COLLAB-01: provider<->client messaging (always visible).
  { title: 'Messagerie', icon: MessageSquare, href: '/messages' },
  // SOCIAL-LAYER (Forum): business-owned discussion forums.
  { title: 'Forums', icon: MessagesSquare, href: '/forum' },
  // Cabinet-level admin (the FIRM's own admin).
  {
    title: 'Administration Cabinet',
    icon: UserCog,
    children: [
      { title: 'Mon Cabinet', icon: Building2, href: '/admin' },
      { title: 'Organisations', icon: Building2, href: '/admin/organizations' },
      { title: 'Hierarchie', icon: Network, href: '/admin/organizations/hierarchy' },
      { title: 'Equipe', icon: Users, href: '/team' },
      { title: 'Roles & Permissions', icon: Lock, href: '/team/roles' },
      { title: 'Invitations', icon: Users, href: '/team/invitations' },
      { title: 'Integrations', icon: Plug, href: '/admin/integrations' },
      { title: 'Cles API', icon: KeyRound, href: '/admin/api-keys' },
      { title: 'Webhooks', icon: Plug, href: '/admin/webhooks' },
      { title: 'Audit & Securite', icon: ShieldCheck, href: '/admin/audit' },
    ],
  },
  // Client-level admin (the SELECTED CLIENT's admin — visible to cabinet
  // members acting on behalf of a client). Only meaningful when a client
  // is selected via the ClientSelector; routes are reused from the same
  // /admin tree (the page reads `useClientContext.activeClientId`).
  {
    title: 'Admin Client',
    icon: Building2,
    children: [
      { title: 'Fiche Client', icon: Building2, href: '/clients' },
      { title: 'Utilisateurs Client', icon: Users, href: '/admin/users' },
      { title: 'Roles Client', icon: Lock, href: '/admin/roles' },
      { title: 'Modules & Contrat', icon: Settings, href: '/account/features' },
      { title: 'Facturation Client', icon: CreditCard, href: '/billing' },
      { title: 'Audit Client', icon: ShieldCheck, href: '/admin/audit' },
    ],
  },
  {
    title: 'Mon Compte',
    icon: CreditCard,
    children: [
      { title: 'Facturation', icon: CreditCard, href: '/billing' },
      { title: 'Plans', icon: Crown, href: '/account/plans' },
      { title: 'Credits IA', icon: BrainCircuit, href: '/account/credits' },
      { title: 'Modules actifs', icon: Settings, href: '/account/features' },
      { title: 'Quotas', icon: Gauge, href: '/account/quotas' },
    ],
  },
  {
    title: 'Parametres',
    icon: Settings,
    children: [
      { title: 'Profil', icon: Users, href: '/settings/profile' },
      { title: 'Apparence', icon: Palette, href: '/settings/appearance' },
      { title: 'Notifications', icon: Settings, href: '/settings/notifications' },
      { title: 'Securite', icon: ShieldCheck, href: '/settings/security' },
      { title: 'Developpeur', icon: Settings, href: '/settings/developer' },
      { title: 'Vie privee', icon: Lock, href: '/settings/privacy' },
      { title: 'Juridictions', icon: Globe, href: '/settings/jurisdictions' },
      { title: 'Champs personnalises', icon: SlidersHorizontal, href: '/settings/custom-fields' },
      { title: 'Comptabilite', icon: Calculator, href: '/settings/accounting' },
      { title: 'Preparation fiscale', icon: Receipt, href: '/settings/tax' },
      { title: 'Audit & Finance', icon: ClipboardCheck, href: '/settings/audit-finance' },
      { title: 'Paie', icon: Banknote, href: '/settings/payroll' },
      { title: 'Assistant IA', icon: Sparkles, href: '/settings/llm' },
    ],
  },
  {
    title: 'Juridique',
    icon: Gavel,
    children: [
      { title: 'Hub Juridique', icon: Gavel, href: '/legal' },
      { title: 'Veille Reglementaire', icon: Eye, href: '/legal/veille-reglementaire' },
      { title: 'Obligations', icon: FileCheck, href: '/legal/obligations-contractuelles' },
      { title: 'Protection Donnees', icon: ShieldCheck, href: '/legal/protection-donnees' },
      { title: 'Contentieux', icon: AlertTriangle, href: '/legal/contentieux' },
      { title: 'Propriete Intellectuelle', icon: BrainCircuit, href: '/legal/propriete-intellectuelle' },
      { title: 'Gouvernance Corp.', icon: Building2, href: '/legal/gouvernance-corporative' },
      { title: 'Droit du Travail', icon: Users, href: '/legal/droit-travail' },
      { title: 'Fiscalite', icon: CreditCard, href: '/legal/fiscalite' },
      { title: 'Environnement', icon: Globe, href: '/legal/environnement' },
    ],
  },
  { title: 'Support', icon: HelpCircle, href: '/support' },
]

// Localize the (French-authored) bottom nav tree for the active language.
function localizeNav(items: NavItem[]): NavItem[] {
  return items.map((item) => ({
    ...item,
    title: tn(item.title),
    ...(item.children ? { children: localizeNav(item.children) } : {}),
  }))
}

// ─── Nav Item Component (dark theme) ───────────────────
function NavItemComponent({
  item, collapsed, openSections, toggleSection,
}: {
  item: NavItem; collapsed: boolean
  openSections: Record<string, boolean>; toggleSection: (title: string) => void
}) {
  const location = useLocation()
  const isChildActive = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  )

  if (item.children && item.children.length > 0) {
    const isOpen = openSections[item.title] || isChildActive
    return (
      <Collapsible open={!collapsed && isOpen}>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <CollapsibleTrigger asChild>
                <button
                  onClick={() => !collapsed && toggleSection(item.title)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium',
                    'transition-all duration-200',
                    isChildActive
                      ? 'bg-[hsl(var(--sidebar-hover-bg))] text-[hsl(var(--sidebar-fg))]'
                      : 'text-[hsl(var(--sidebar-fg))]/60 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
                    collapsed && 'justify-center'
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.title}</span>
                      <ChevronDown className={cn(
                        'h-3.5 w-3.5 transition-transform duration-200',
                        isOpen && 'rotate-180'
                      )} />
                    </>
                  )}
                </button>
              </CollapsibleTrigger>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{item.title}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
        <CollapsibleContent>
          <div className="ml-5 mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-3">
            {item.children.map((child) => (
              <NavLink
                key={child.href}
                to={child.href!}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12px]',
                    'transition-all duration-200',
                    isActive
                      ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))] font-semibold shadow-md shadow-primary/25'
                      : 'text-[hsl(var(--sidebar-fg))]/50 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]'
                  )
                }
              >
                <child.icon className="h-3.5 w-3.5 shrink-0" />
                <span>{child.title}</span>
              </NavLink>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.href!}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium',
                'transition-all duration-200',
                isActive
                  ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))] shadow-md shadow-primary/25'
                  : 'text-[hsl(var(--sidebar-fg))]/60 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
                collapsed && 'justify-center'
              )
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span>{item.title}</span>}
          </NavLink>
        </TooltipTrigger>
        {collapsed && <TooltipContent side="right">{item.title}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Category Section (collapsible group of context sections) ─────────────
//
// Wraps multiple "sections" under a single top-level category that is
// collapsed by default. When the user opens it, the inner sections (already
// individually collapsible via NavItemComponent) become visible.

interface CategorySectionProps {
  title: string
  icon: React.ElementType
  collapsed: boolean
  sections: { id: string; label: string; items: { title: string; icon: React.ElementType; href?: string; children?: any[] }[] }[]
  extraItems?: NavItem[]
  openSections: Record<string, boolean>
  toggleSection: (title: string) => void
  defaultOpen?: boolean
}

function CategorySection({
  title,
  icon: Icon,
  collapsed,
  sections,
  extraItems,
  openSections,
  toggleSection,
  defaultOpen = false,
}: CategorySectionProps) {
  const location = useLocation()
  const allItems = sections.flatMap((s) => s.items)
  const hasActiveChild =
    allItems.some(
      (item) =>
        (item.href && location.pathname.startsWith(item.href)) ||
        item.children?.some(
          (c: any) => c.href && location.pathname.startsWith(c.href),
        ),
    ) ||
    extraItems?.some(
      (item) =>
        (item.href && location.pathname.startsWith(item.href)) ||
        item.children?.some(
          (c) => c.href && location.pathname.startsWith(c.href),
        ),
    )

  const isOpen = openSections[`__cat_${title}`] ?? (defaultOpen || hasActiveChild)

  if (collapsed) {
    // Collapsed sidebar: skip category headers, just render items inline
    return (
      <>
        {sections.flatMap((s) =>
          s.items.map((item) => (
            <NavItemComponent
              key={item.title + (item.href || '')}
              item={item as NavItem}
              collapsed
              openSections={openSections}
              toggleSection={toggleSection}
            />
          )),
        )}
        {extraItems?.map((item) => (
          <NavItemComponent
            key={item.title}
            item={item}
            collapsed
            openSections={openSections}
            toggleSection={toggleSection}
          />
        ))}
      </>
    )
  }

  return (
    <Collapsible open={isOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          onClick={() => toggleSection(`__cat_${title}`)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-3 py-2 mt-3 first:mt-0',
            'text-[11px] font-bold uppercase tracking-[0.15em] transition-colors',
            hasActiveChild
              ? 'text-[hsl(var(--sidebar-fg))]'
              : 'text-[hsl(var(--sidebar-section-fg))] hover:text-[hsl(var(--sidebar-fg))]',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">{title}</span>
          <ChevronDown
            className={cn(
              'h-3 w-3 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-0.5 pt-1">
          {sections.map((section) => (
            <div key={section.id}>
              <div className="flex items-center gap-2 px-3 py-1.5">
                <span className="text-[9px] font-semibold text-[hsl(var(--sidebar-section-fg))] uppercase tracking-[0.15em]">
                  {section.label}
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-[hsl(var(--sidebar-border))] to-transparent" />
              </div>
              {section.items.map((item) => (
                <NavItemComponent
                  key={item.title + (item.href || '')}
                  item={item as NavItem}
                  collapsed={false}
                  openSections={openSections}
                  toggleSection={toggleSection}
                />
              ))}
            </div>
          ))}
          {extraItems?.map((item) => (
            <NavItemComponent
              key={item.title}
              item={item}
              collapsed={false}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ─── Main Sidebar ──────────────────────────────────────
interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

// ─── Category mapping ────────────────────────────────────
//
// Each context section belongs to exactly one top-level category in the
// sidebar. PROJECT items lead because they answer "what am I working on?";
// FEATURE items follow with the bulk of the work; TOOLS and ADMIN come last.

const PROJECT_SECTION_IDS = new Set<NavSectionId>([
  'mes-mandats', 'mes-missions', 'clients', 'planification', 'suivi-qualite',
])
const TOOLS_SECTION_IDS = new Set<NavSectionId>([
  'workspace', 'rapports',
])
const ADMIN_SECTION_IDS = new Set<NavSectionId>([
  'mon-cabinet', 'equipe', 'facturation', 'integrations', 'juridique', 'parametres', 'ma-page',
])

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const location = useLocation()
  const { activeContext } = useContextStore()

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }))
  }

  // Re-derive on language change (t/i18n subscription drives the re-render).
  const localizedBottomNav = localizeNav(bottomNavItems)
  const contextSections = getNavSectionsForContext(activeContext)
  const projectSections = contextSections.filter((s) => PROJECT_SECTION_IDS.has(s.id))
  const toolsSections = contextSections.filter((s) => TOOLS_SECTION_IDS.has(s.id))
  const adminSections = contextSections.filter((s) => ADMIN_SECTION_IDS.has(s.id))
  const featureSections = contextSections.filter(
    (s) =>
      !PROJECT_SECTION_IDS.has(s.id) &&
      !TOOLS_SECTION_IDS.has(s.id) &&
      !ADMIN_SECTION_IDS.has(s.id),
  )

  const isHubActive = location.pathname === '/hub'
  const isDashboardActive = location.pathname === '/dashboard'

  return (
    <nav
      aria-label="Sidebar navigation"
      className={cn(
        'flex h-screen flex-col glass-sidebar transition-all duration-300',
        collapsed ? 'w-[68px]' : 'w-[260px]'
      )}
    >
      {/* Header: Logo + PRO badge */}
      <div className="flex h-[56px] items-center justify-between px-4 shrink-0 border-b border-[hsl(var(--sidebar-border))]">
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-400 shadow-lg shadow-primary/20">
              <span className="text-white font-black text-sm">AC</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-[15px] text-[hsl(var(--sidebar-fg))]">AdminClient</span>
              <span className="text-[9px] font-bold bg-gradient-to-r from-primary to-blue-400 text-white px-1.5 py-0.5 rounded-md uppercase tracking-wider">Pro</span>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex h-9 w-9 mx-auto items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-400 shadow-lg shadow-primary/20">
            <span className="text-white font-black text-sm">AC</span>
          </div>
        )}
      </div>

      {/* Context dropdown moved to the top bar — sidebar stays clean. */}

      {/* Scrollable Navigation */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="flex flex-col gap-0.5 p-3">

          {/* ── Top-level direct links ────────────────────────── */}
          <NavLink
            to="/dashboard"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all',
              isDashboardActive
                ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))] shadow-md shadow-primary/25'
                : 'text-[hsl(var(--sidebar-fg))]/70 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
              collapsed && 'justify-center',
            )}
          >
            <LayoutDashboard className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span>{t('nav.dashboard')}</span>}
          </NavLink>

          <NavLink
            to="/hub"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all',
              isHubActive
                ? 'bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))] shadow-md shadow-primary/25'
                : 'text-[hsl(var(--sidebar-fg))]/70 hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))]',
              collapsed && 'justify-center',
            )}
          >
            <LayoutGrid className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span>{t('nav.hub')}</span>}
          </NavLink>

          {/* ── Projet category (only when applicable) ─────────── */}
          {projectSections.length > 0 && (
            <CategorySection
              title={t('nav.categories.projet')}
              icon={Briefcase}
              collapsed={collapsed}
              sections={projectSections}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          )}

          {/* ── Fonctionnalités category ───────────────────────── */}
          {featureSections.length > 0 && (
            <CategorySection
              title={t('nav.categories.fonctionnalites')}
              icon={LayoutGrid}
              collapsed={collapsed}
              sections={featureSections}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          )}

          {/* ── Outils ─────────────────────────────────────────── */}
          {toolsSections.length > 0 && (
            <CategorySection
              title={t('nav.categories.outils')}
              icon={Wrench}
              collapsed={collapsed}
              sections={toolsSections}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          )}

          {/* ── Administration ─────────────────────────────────── */}
          {(adminSections.length > 0 || bottomNavItems.length > 0) && (
            <CategorySection
              title={t('nav.categories.administration')}
              icon={UserCog}
              collapsed={collapsed}
              sections={adminSections}
              extraItems={localizedBottomNav}
              openSections={openSections}
              toggleSection={toggleSection}
              defaultOpen={false}
            />
          )}
        </div>
      </ScrollArea>

      {/* Collapse toggle */}
      <div className="shrink-0 border-t border-[hsl(var(--sidebar-border))] p-2">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-center gap-2 rounded-lg p-2 text-[hsl(var(--sidebar-section-fg))] hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-[hsl(var(--sidebar-fg))] transition-all duration-200"
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4" />
            : <><ChevronLeft className="h-4 w-4" />{<span className="text-[11px]">{t('nav.collapse')}</span>}</>
          }
        </button>
      </div>
    </nav>
  )
}
